import React, { useState, useEffect, useCallback } from 'react';
import {
  Package, Trash2, QrCode, RefreshCw, X, MapPin, Clock, Hash, Edit2, Check,
  ExternalLink, MessageCircle, Smartphone, Lock,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import apiClient, { API_BASE_URL } from '../../api/apiClient';
import { Parcel, CARRIER_LABELS, ShowNotificationFn, VINTED_MESSAGING_URL } from './crm.types';
import { CrmEmailSyncBanner } from './CrmEmailSyncBanner';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso?: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' });
}

function daysUntil(iso?: string): number | null {
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  return Math.ceil(diff / 86400000);
}

function isAwaitingPickup(p: Parcel): boolean {
  return p.status === 'waiting' || p.status === 'in_transit';
}

function deadlineBadge(iso?: string) {
  const d = daysUntil(iso);
  if (d === null) return null;
  if (d < 0)  return { text: 'Expiré',         cls: 'bg-red-100 text-red-700' };
  if (d === 0) return { text: "Aujourd'hui !",  cls: 'bg-red-100 text-red-700' };
  if (d <= 2)  return { text: `J-${d}`,         cls: 'bg-orange-100 text-orange-700' };
  return         { text: `J-${d}`,              cls: 'bg-amber-100 text-amber-700' };
}

// ── Deadline block ────────────────────────────────────────────────────────────

interface DeadlineBlockProps {
  parcel: Parcel;
  onUpdate: (id: number, deadline: string) => Promise<void>;
}

const DeadlineBlock: React.FC<DeadlineBlockProps> = ({ parcel, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [dateVal, setDateVal] = useState('');

  const handleSave = async () => {
    if (!dateVal) return;
    await onUpdate(parcel.id, new Date(dateVal).toISOString());
    setEditing(false);
  };

  if (editing) {
    return (
      <div
        className="flex-shrink-0 flex flex-col items-end gap-1 min-w-[7rem]"
        onClick={e => e.stopPropagation()}
      >
        <input
          type="date"
          autoFocus
          className="border border-violet-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-violet-500"
          value={dateVal}
          onChange={e => setDateVal(e.target.value)}
        />
        <div className="flex gap-1">
          <button onClick={handleSave} className="text-violet-600 hover:text-violet-800"><Check size={13} /></button>
          <button onClick={() => setEditing(false)} className="text-gray-400 hover:text-gray-600"><X size={13} /></button>
        </div>
      </div>
    );
  }

  if (parcel.pickup_deadline) {
    const d = daysUntil(parcel.pickup_deadline);
    const isExpired = d !== null && d < 0;
    const isToday   = d === 0;
    const isUrgent  = d !== null && d > 0 && d <= 2;
    const numCls = isExpired || isToday ? 'text-red-600' : isUrgent ? 'text-orange-500' : 'text-amber-500';
    return (
      <div
        className="flex-shrink-0 flex flex-col items-end justify-center gap-0.5 min-w-[5rem] cursor-pointer group"
        onClick={e => { e.stopPropagation(); setDateVal(''); setEditing(true); }}
      >
        <span className={`text-2xl font-black leading-none ${numCls}`}>
          {isExpired ? 'EXP.' : isToday ? 'Auj.' : `J-${d}`}
        </span>
        <span className="flex items-center gap-0.5 text-[10px] text-gray-400 group-hover:text-violet-400 transition-colors">
          <Clock size={9} />
          {formatDate(parcel.pickup_deadline)}
          <Edit2 size={8} className="opacity-0 group-hover:opacity-100 transition-opacity" />
        </span>
      </div>
    );
  }

  return (
    <div
      className="flex-shrink-0 flex flex-col items-end justify-center gap-1 min-w-[5rem] cursor-pointer"
      onClick={e => { e.stopPropagation(); setDateVal(''); setEditing(true); }}
    >
      <span className="text-[10px] text-gray-300 hover:text-violet-400 transition-colors flex items-center gap-0.5">
        <Clock size={9} /> Définir date
      </span>
    </div>
  );
};

// ── QR Modal ─────────────────────────────────────────────────────────────────

interface QrModalProps {
  parcels: Parcel[];
  onClose: () => void;
  showNotification: ShowNotificationFn;
}

function parcelTransitOrNoQr(parcel: Parcel): boolean {
  return parcel.status === 'in_transit' || (parcel.carrier === 'vinted_go' && !parcel.qr_code_url);
}

interface PhoneQrShareModalProps {
  parcels: Parcel[];
  onClose: () => void;
  showNotification: ShowNotificationFn;
}

/** Pop-up séparée : lien + QR à scanner sur le téléphone (au-dessus de la modale « QR codes de retrait »). */
const PhoneQrShareModal: React.FC<PhoneQrShareModalProps> = ({ parcels, onClose, showNotification }) => {
  const [phoneShareUrl, setPhoneShareUrl] = useState<string | null>(null);
  const [phoneShareLoading, setPhoneShareLoading] = useState(false);

  const generatePhoneShareLink = async () => {
    if (!parcels.length) return;
    setPhoneShareLoading(true);
    try {
      const res = await apiClient.post('/crm/parcels/qr-share-token', {
        parcel_ids: parcels.map(p => p.id),
      });
      const token = res.data?.token as string | undefined;
      if (!token) throw new Error('no token');
      const u = `${API_BASE_URL}/crm/parcels/qr-share/view?t=${encodeURIComponent(token)}`;
      setPhoneShareUrl(u);
      showNotification('Lien prêt', 'Scannez le QR avec votre téléphone.', 'success');
    } catch {
      showNotification('Erreur', 'Impossible de générer le lien pour mobile.', 'error');
    } finally {
      setPhoneShareLoading(false);
    }
  };

  const copyPhoneLink = async () => {
    if (!phoneShareUrl) return;
    try {
      await navigator.clipboard.writeText(phoneShareUrl);
      showNotification('Copié', 'Lien collé dans le presse-papiers.', 'success');
    } catch {
      showNotification('Erreur', 'Copie impossible (navigateur).', 'error');
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[220] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Smartphone size={18} className="text-teal-600" />
            <h3 className="text-base font-bold text-gray-900">Ouvrir sur le téléphone</h3>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
            <X size={20} />
          </button>
        </div>
        <div className="px-5 py-4 space-y-4">
          <p className="text-xs text-gray-600 leading-relaxed">
            Génère un lien unique (environ 4 h) puis scannez le QR avec l&apos;appareil photo — même contenu que dans l&apos;autre fenêtre.
          </p>
          <button
            type="button"
            onClick={generatePhoneShareLink}
            disabled={phoneShareLoading || parcels.length === 0}
            className="w-full px-3 py-2.5 rounded-xl bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50 disabled:pointer-events-none"
          >
            {phoneShareLoading ? 'Génération…' : phoneShareUrl ? 'Régénérer le lien' : 'Générer le lien'}
          </button>
          {phoneShareUrl && (
            <div className="flex flex-col items-center pt-2 border-t border-gray-100">
              <div className="bg-white p-3 rounded-xl border-2 border-teal-700 shadow-sm">
                <QRCodeSVG value={phoneShareUrl} size={200} level="M" />
              </div>
              <p className="text-[11px] text-teal-900/80 text-center mt-3 max-w-sm">
                Scannez avec l&apos;appareil photo. Ne partagez pas ce QR : il donne accès à vos codes de retrait.
              </p>
              <button
                type="button"
                onClick={copyPhoneLink}
                className="mt-2 text-xs font-medium text-teal-700 hover:text-teal-900 underline"
              >
                Copier le lien
              </button>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
};

const QrModal: React.FC<QrModalProps> = ({ parcels, onClose, showNotification }) => {
  const [showPhoneModal, setShowPhoneModal] = useState(false);

  // Grouper par lieu de retrait
  const groups = React.useMemo(() => {
    const map = new Map<string, Parcel[]>();
    for (const p of parcels) {
      const key = p.pickup_location_name || p.pickup_address || 'Point relais';
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(p);
    }
    return Array.from(map.entries());
  }, [parcels]);

  return (
    <>
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100 flex-shrink-0 gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <QrCode size={18} className="text-violet-600 flex-shrink-0" />
            <h3 className="text-base font-bold text-gray-900 truncate">QR codes de retrait</h3>
            <span className="text-xs text-gray-400 font-medium flex-shrink-0">({parcels.length})</span>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => setShowPhoneModal(true)}
              disabled={parcels.length === 0}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-white text-sm font-bold shadow-lg shadow-teal-500/35 ring-2 ring-teal-400/60 ring-offset-2 ring-offset-white bg-gradient-to-r from-teal-500 via-teal-600 to-emerald-600 hover:from-teal-600 hover:via-teal-700 hover:to-emerald-700 hover:shadow-xl hover:shadow-teal-600/25 transition-all disabled:opacity-40 disabled:pointer-events-none disabled:shadow-none disabled:ring-0"
              title="Lien + QR pour ouvrir sur le téléphone"
            >
              <Smartphone size={17} className="opacity-95" />
              Sur téléphone
            </button>
            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors p-1">
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Contenu scrollable */}
        <div className="overflow-y-auto flex-1 px-6 py-4 space-y-8">
          {groups.map(([locationKey, groupParcels]) => (
            <div key={locationKey}>
              {/* En-tête lieu */}
              <div className="flex items-start gap-2 mb-4">
                <MapPin size={15} className="text-violet-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-bold text-gray-800">{locationKey}</p>
                  {groupParcels[0]?.pickup_address && groupParcels[0].pickup_address !== locationKey && (
                    <p className="text-xs text-gray-400">{groupParcels[0].pickup_address}</p>
                  )}
                  {groupParcels[0]?.opening_hours && (
                    <p className="text-xs text-gray-500 flex items-start gap-1 mt-0.5 break-words min-w-0">
                      <Clock size={10} className="flex-shrink-0 mt-0.5" />
                      <span className="break-words">{groupParcels[0].opening_hours}</span>
                    </p>
                  )}
                </div>
              </div>

              {/* Colis du groupe */}
              <div className="space-y-6">
                {groupParcels.map(parcel => {
                  const cl = CARRIER_LABELS[parcel.carrier] || { label: parcel.carrier, color: 'text-gray-600', bg: 'bg-gray-100', border: 'border-gray-200' };
                  const badge = deadlineBadge(parcel.pickup_deadline);
                  return (
                    <div key={parcel.id} className={`border rounded-xl p-4 ${cl.border}`}>
                      {/* Infos colis */}
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1 min-w-0">
                          <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-1 ${cl.bg} ${cl.color}`}>
                            {cl.label}
                          </span>
                          <p className="text-sm font-semibold text-gray-800 truncate">
                            {parcel.item_title || parcel.tracking_number || 'Colis'}
                          </p>
                          {parcel.tracking_number && (
                            <p className="text-xs text-gray-400">Suivi : {parcel.tracking_number}</p>
                          )}
                        </div>
                        {badge && (
                          <span className={`text-xs font-bold px-2 py-1 rounded-lg flex-shrink-0 ml-3 ${badge.cls}`}>
                            {badge.text}
                          </span>
                        )}
                      </div>

                      {/* QR / Code selon transporteur */}
                      {parcel.carrier === 'mondial_relay' ? (
                        <div className="flex flex-col items-center py-4">
                          <p className="text-xs text-gray-400 mb-3 font-medium uppercase tracking-wide">
                            Code de retrait Locker
                          </p>
                          <div className="bg-pink-50 border-2 border-pink-300 rounded-2xl px-10 py-5 text-center">
                            <span className="text-5xl font-black tracking-[0.15em] text-pink-700 font-mono">
                              {parcel.pickup_code || '—'}
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mt-3">
                            Saisir ce code sur l'écran du Locker Mondial Relay
                          </p>
                        </div>
                      ) : parcelTransitOrNoQr(parcel) ? (
                        <div className="flex flex-col items-center py-4 gap-3 text-center">
                          <p className="text-xs text-gray-500 max-w-sm">
                            {parcel.status === 'in_transit'
                              ? 'Colis en route : le QR de retrait est généralement disponible à l’arrivée au point relais.'
                              : 'Le QR code sera disponible au relais une fois le colis prêt.'}
                          </p>
                          <div className="flex flex-wrap items-center justify-center gap-2">
                            {parcel.tracking_url && (
                              <a
                                href={parcel.tracking_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-teal-600 text-white text-xs font-semibold hover:bg-teal-700"
                              >
                                <ExternalLink size={13} />
                                Suivi colis
                              </a>
                            )}
                            {parcel.carrier === 'vinted_go' && (
                              <a
                                href={VINTED_MESSAGING_URL}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-teal-300 text-teal-800 text-xs font-semibold hover:bg-teal-50"
                              >
                                <MessageCircle size={13} />
                                Messagerie Vinted
                              </a>
                            )}
                          </div>
                          {parcel.estimated_delivery_date && (
                            <p className="text-xs text-gray-400">
                              Livraison estimée : {formatDate(parcel.estimated_delivery_date)}
                            </p>
                          )}
                          {parcel.pickup_code && (
                            <p className="text-sm text-gray-500">
                              Code : <strong className="text-gray-800">{parcel.pickup_code}</strong>
                            </p>
                          )}
                        </div>
                      ) : parcel.qr_code_url ? (
                        <div className="flex flex-col items-center py-4">
                          <p className="text-xs text-gray-400 mb-3 font-medium uppercase tracking-wide">
                            QR code de retrait
                          </p>
                          <div className="bg-white border-4 border-gray-800 rounded-2xl p-3">
                            <img
                              src={parcel.qr_code_url}
                              alt="QR code"
                              className="w-52 h-52 object-contain"
                              onError={e => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          </div>
                          {parcel.pickup_code && (
                            <p className="text-sm text-gray-500 mt-3">
                              Code alternatif : <strong className="text-gray-800">{parcel.pickup_code}</strong>
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center py-6 text-gray-400 text-sm">
                          Aucun QR code disponible
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={onClose}
            className="w-full py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
    {showPhoneModal && (
      <PhoneQrShareModal
        parcels={parcels}
        onClose={() => setShowPhoneModal(false)}
        showNotification={showNotification}
      />
    )}
    </>
  );
};

// ── Popup "aucune sélection" ──────────────────────────────────────────────────

const NoSelectionPopup: React.FC<{ message: string; onClose: () => void }> = ({ message, onClose }) => (
  <div className="fixed inset-0 bg-black/40 z-[300] flex items-center justify-center p-4" onClick={onClose}>
    <div
      className="bg-white rounded-2xl shadow-2xl px-8 py-6 max-w-sm w-full text-center"
      onClick={e => e.stopPropagation()}
    >
      <p className="text-sm font-semibold text-gray-800 mb-1">Aucun colis sélectionné</p>
      <p className="text-xs text-gray-500 mb-4">{message}</p>
      <button
        onClick={onClose}
        className="px-5 py-2 rounded-xl bg-violet-600 text-white text-sm font-medium hover:bg-violet-700 transition-colors"
      >
        OK
      </button>
    </div>
  </div>
);

// ── Page principale ───────────────────────────────────────────────────────────

interface CrmReceptionProps {
  showNotification: ShowNotificationFn;
  crm_email_active?: boolean;
  is_free_plan?: boolean;
  onUpgrade?: () => void;
}

const CrmReception: React.FC<CrmReceptionProps> = ({ showNotification, crm_email_active, is_free_plan, onUpgrade }) => {
  const [parcels, setParcels]             = useState<Parcel[]>([]);
  const [loading, setLoading]             = useState(true);
  const [selectedIds, setSelectedIds]     = useState<Set<number>>(new Set());
  const [showQrModal, setShowQrModal]     = useState(false);
  const [filterStatus, setFilterStatus]   = useState<'waiting' | 'picked_up' | 'all'>('waiting');
  const [noSelectionMsg, setNoSelectionMsg] = useState<string | null>(null);

  const fetchParcels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/crm/parcels');
      const raw = res.data;
      setParcels(Array.isArray(raw) ? raw : []);
    } catch {
      showNotification('Erreur', 'Impossible de charger les colis', 'error');
    } finally {
      setLoading(false);
    }
  }, [showNotification]);

  useEffect(() => { fetchParcels(); }, [fetchParcels]);

  const filtered = parcels.filter(p => {
    if (filterStatus === 'all') return true;
    if (filterStatus === 'picked_up') return p.status === 'picked_up';
    return isAwaitingPickup(p);
  });

  const toggleSelect = (id: number) =>
    setSelectedIds(prev => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });

  const toggleAll = () => {
    if (selectedIds.size === filtered.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filtered.map(p => p.id)));
    }
  };

  const handleDelete = async () => {
    if (selectedIds.size === 0) {
      setNoSelectionMsg('Cochez des colis dans la liste pour pouvoir les supprimer.');
      return;
    }
    const confirmed = window.confirm(`Supprimer ${selectedIds.size} colis ?`);
    if (!confirmed) return;
    try {
      await Promise.all(
        Array.from(selectedIds).map(id => apiClient.delete(`/crm/parcels/${id}`))
      );
      setSelectedIds(new Set());
      fetchParcels();
      showNotification('Succès', `${selectedIds.size} colis supprimé(s)`, 'success');
    } catch {
      showNotification('Erreur', 'Impossible de supprimer', 'error');
    }
  };

  const handleMarkPickedUp = async () => {
    if (selectedIds.size === 0) {
      setNoSelectionMsg('Cochez des colis dans la liste pour les marquer comme récupérés.');
      return;
    }
    try {
      await Promise.all(
        Array.from(selectedIds).map(id =>
          apiClient.patch(`/crm/parcels/${id}`, { status: 'picked_up' })
        )
      );
      setSelectedIds(new Set());
      fetchParcels();
      showNotification('Succès', 'Colis marqués comme récupérés', 'success');
    } catch {
      showNotification('Erreur', 'Impossible de mettre à jour', 'error');
    }
  };

  const handleShowQr = () => {
    if (selectedIds.size === 0) {
      setNoSelectionMsg('Cochez des colis dans la liste pour visualiser leurs QR codes.');
      return;
    }
    setShowQrModal(true);
  };

  // Colis à afficher dans le QR modal : sélectionnés ou tous les "waiting"
  const qrParcels = selectedIds.size > 0
    ? filtered.filter(p => selectedIds.has(p.id))
    : filtered.filter(isAwaitingPickup);

  const waitingCount = parcels.filter(isAwaitingPickup).length;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Header */}
      <div className="px-8 pt-8 pb-4 flex-shrink-0">
        <div className="flex flex-wrap items-start justify-between gap-4 mb-1">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Package size={22} className="text-violet-600" />
              Réception
            </h1>
            <p className="text-sm text-gray-500 mt-0.5 flex items-center">
              {waitingCount} colis en attente (transit ou prêt à retirer)
              {is_free_plan && (
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ml-2 ${filtered.length >= 10 ? 'bg-red-100 text-red-600' : 'bg-violet-100 text-violet-700'}`}>
                  {Math.min(filtered.length, 10)} / 10
                </span>
              )}
            </p>
          </div>
          <div className="flex flex-col items-stretch sm:items-end gap-2 w-full sm:w-auto min-w-0 max-w-xl">
            {!crm_email_active && <CrmEmailSyncBanner />}
            <div className="flex justify-end">
              <button
                onClick={fetchParcels}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
                title="Actualiser"
              >
                <RefreshCw size={17} />
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mt-4">
          {([
            { key: 'waiting',   label: 'En attente' },
            { key: 'picked_up', label: 'Récupérés'  },
            { key: 'all',       label: 'Tous'        },
          ] as const).map(tab => (
            <button
              key={tab.key}
              onClick={() => { setFilterStatus(tab.key); setSelectedIds(new Set()); }}
              className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                filterStatus === tab.key
                  ? 'bg-violet-600 text-white shadow-sm'
                  : 'text-gray-500 hover:bg-gray-100'
              }`}
            >
              {tab.label}
              {tab.key === 'waiting' && waitingCount > 0 && (
                <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                  filterStatus === 'waiting' ? 'bg-white/20 text-white' : 'bg-violet-100 text-violet-700'
                }`}>{waitingCount}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Barre d'actions */}
      <div className="px-8 pb-4 flex-shrink-0 flex items-center justify-between">
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={selectedIds.size === filtered.length && filtered.length > 0}
            onChange={toggleAll}
            className="w-4 h-4 rounded border-gray-300 accent-violet-600"
          />
          <span className="text-xs text-gray-500 font-medium">
            {selectedIds.size > 0
              ? `${selectedIds.size} sélectionné${selectedIds.size > 1 ? 's' : ''}`
              : 'Tout sélectionner'}
          </span>
        </label>

        <div className="flex items-center gap-2">
          {/* Onglet "En attente" : QR + Marquer récupéré + Supprimer */}
          {filterStatus === 'waiting' && (
            <>
              <button
                onClick={handleShowQr}
                className="flex items-center gap-2 px-4 py-2 rounded-xl border border-violet-300 text-violet-700 bg-violet-50 hover:bg-violet-100 text-sm font-medium transition-all"
              >
                <QrCode size={15} />
                Visualiser QR codes
                {qrParcels.length > 0 && (
                  <span className="text-xs bg-violet-200 text-violet-800 px-1.5 py-0.5 rounded-full">{qrParcels.length}</span>
                )}
              </button>
              <button
                onClick={handleMarkPickedUp}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-all"
              >
                Marquer récupéré{selectedIds.size > 1 ? 's' : ''}
              </button>
            </>
          )}

          {/* Supprimer (tous les onglets) */}
          <button
            onClick={handleDelete}
            className="flex items-center gap-2 px-4 py-2 rounded-xl border border-red-200 text-red-600 bg-red-50 hover:bg-red-100 text-sm font-medium transition-all"
          >
            <Trash2 size={15} />
            Supprimer
          </button>
        </div>
      </div>

      {/* Liste */}
      <div className="flex-1 overflow-y-auto px-8 pb-8">
        {loading ? (
          <div className="flex items-center justify-center py-20 text-gray-400">
            <RefreshCw size={20} className="animate-spin mr-2" />
            Chargement…
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <Package size={40} className="mb-3 opacity-30" />
            <p className="text-sm font-medium">Aucun colis{filterStatus === 'waiting' ? ' en attente' : ''}</p>
            <p className="text-xs mt-1 opacity-70">
              Les emails des transporteurs (MR, Vinted Go, Chronopost) seront automatiquement parsés
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {is_free_plan && filtered.length > 10 && (
              <div className="relative mb-2">
                <div className="blur-sm pointer-events-none select-none space-y-3">
                  {filtered.slice(0, filtered.length - 10).map(parcel => {
                    const cl = CARRIER_LABELS[parcel.carrier] || { label: parcel.carrier, color: 'text-gray-600', bg: 'bg-gray-100', border: 'border-gray-200' };
                    return (
                      <div key={parcel.id} className="rounded-2xl border-2 border-gray-100 bg-white p-4">
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cl.bg}`}>
                            <Package size={18} className={cl.color} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-gray-800 truncate">
                              {parcel.item_title || parcel.tracking_number || 'Colis sans titre'}
                            </p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-5 text-center shadow-xl border border-gray-100 mx-4 max-w-xs">
                    <div className="w-12 h-12 rounded-full bg-violet-50 flex items-center justify-center mx-auto mb-3">
                      <Lock size={22} className="text-violet-500" />
                    </div>
                    <h4 className="font-bold text-gray-900 mb-1">Limite gratuite atteinte</h4>
                    <p className="text-xs text-gray-500 mb-4">Passez à Starter pour un CRM illimité.</p>
                    <button onClick={onUpgrade} className="px-5 py-2 rounded-xl bg-gradient-to-r from-violet-500 to-purple-600 text-white text-sm font-semibold hover:opacity-90 transition-all shadow">
                      Voir les plans →
                    </button>
                  </div>
                </div>
              </div>
            )}
            {(is_free_plan && filtered.length > 10 ? filtered.slice(filtered.length - 10) : filtered).map(parcel => {
              const cl = CARRIER_LABELS[parcel.carrier] || { label: parcel.carrier, color: 'text-gray-600', bg: 'bg-gray-100', border: 'border-gray-200' };
              const badge = deadlineBadge(parcel.pickup_deadline);
              const selected = selectedIds.has(parcel.id);

              return (
                <div
                  key={parcel.id}
                  onClick={() => toggleSelect(parcel.id)}
                  className={`rounded-2xl border-2 p-4 cursor-pointer transition-all select-none ${
                    selected
                      ? 'border-violet-400 bg-violet-50'
                      : `border-gray-100 bg-white hover:border-gray-200`
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Checkbox */}
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => toggleSelect(parcel.id)}
                      onClick={e => e.stopPropagation()}
                      className="mt-1 w-4 h-4 rounded border-gray-300 accent-violet-600 flex-shrink-0 cursor-pointer"
                    />

                    {/* Icône transporteur */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${cl.bg}`}>
                      <Package size={18} className={cl.color} />
                    </div>

                    {/* Infos */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${cl.bg} ${cl.color}`}>
                          {cl.label}
                        </span>
                        {parcel.status === 'in_transit' && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-sky-50 text-sky-800">
                            En transit
                          </span>
                        )}
                        {parcel.status === 'waiting' && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-violet-50 text-violet-800">
                            Prêt à retirer
                          </span>
                        )}
                        {parcel.status === 'picked_up' && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700">
                            Récupéré
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-bold text-gray-800 truncate">
                        {parcel.item_title || parcel.tracking_number || 'Colis sans titre'}
                      </p>
                      <div className="flex items-center gap-4 mt-1 flex-wrap">
                        {(parcel.pickup_location_name || parcel.pickup_address) && (
                          <span className="flex flex-col gap-0.5 text-xs text-gray-500 max-w-full">
                            <span className="flex items-center gap-1">
                              <MapPin size={11} className="flex-shrink-0" />
                              <span className="truncate">{parcel.pickup_location_name || parcel.pickup_address}</span>
                            </span>
                            {parcel.pickup_location_name && parcel.pickup_address && (
                              <span className="text-[11px] text-gray-400 pl-4">{parcel.pickup_address}</span>
                            )}
                            {parcel.opening_hours && (
                              <span className="text-[11px] text-gray-400 pl-4 flex items-center gap-1">
                                <Clock size={9} className="flex-shrink-0" />
                                {parcel.opening_hours}
                              </span>
                            )}
                          </span>
                        )}
                        {parcel.pickup_code && (
                          <span className="flex items-center gap-1 text-xs text-gray-500 font-mono">
                            <Hash size={11} />
                            {parcel.pickup_code}
                          </span>
                        )}
                        {parcel.status === 'in_transit' && parcel.estimated_delivery_date && (
                          <span className="text-xs text-sky-700">
                            Estimé : {formatDate(parcel.estimated_delivery_date)}
                          </span>
                        )}
                      </div>
                      {(parcel.tracking_url || parcel.carrier === 'vinted_go') && (
                        <div
                          className="flex flex-wrap items-center gap-2 mt-2"
                          onClick={e => e.stopPropagation()}
                        >
                          {parcel.tracking_url && (
                            <a
                              href={parcel.tracking_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:text-teal-900"
                            >
                              <ExternalLink size={12} />
                              Suivi
                            </a>
                          )}
                          {parcel.carrier === 'vinted_go' && (
                            <a
                              href={VINTED_MESSAGING_URL}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:text-teal-900"
                            >
                              <MessageCircle size={12} />
                              Messagerie
                            </a>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Deadline en évidence + édition */}
                    <DeadlineBlock parcel={parcel} onUpdate={async (id, deadline) => {
                      await apiClient.patch(`/crm/parcels/${id}`, { pickup_deadline: deadline });
                      fetchParcels();
                    }} />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal QR */}
      {showQrModal && (
        <QrModal
          parcels={qrParcels}
          onClose={() => setShowQrModal(false)}
          showNotification={showNotification}
        />
      )}

      {/* Popup aucune sélection */}
      {noSelectionMsg && (
        <NoSelectionPopup
          message={noSelectionMsg}
          onClose={() => setNoSelectionMsg(null)}
        />
      )}
    </div>
  );
};

export default CrmReception;
